import { addMinutes, endOfISOWeek, format, startOfISOWeek } from 'date-fns';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Brush,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ReactComponent as CourseDownIcon } from 'assets/images/icons/course_down.svg';
import { ReactComponent as CourseUpIcon } from 'assets/images/icons/course_up.svg';

import ButtonGroup from 'ui/ButtonGroup';
import Card from 'ui/Card';
import Loader from 'ui/Loader';

import { StatisticsSummary } from 'modules/Dashboard/StatisticsSummary';
import CustomTooltip from 'modules/SensorDetails/CustomTooltip';
import DateChanger from 'modules/SensorDetails/DateChanger';
import InfoCard from 'modules/SensorDetails/InfoCard';

import { DateTypes, SensorTabs } from 'entities/view/sensorDetails';

import { IRootStore } from 'store/Root.reducer';
import {
  getSensorDetailFullChartRequest,
  getSensorMetricsRequest,
  resetSensorDetails,
} from 'store/SensorDetails/SensorDetails.action';

import {
  PLANT_NAMES,
  PLANT_TYPES,
  QUERY_PARAMS,
  SENSOR_TYPES,
  SensorMeasurement,
} from 'utils/constants';
import {
  formatLocalClock,
  formatLocalDateTime,
  formatLocalDayMonth,
  getBrowserTimeZone,
  parseServerDate,
  QueryHelper,
  StringHelper,
} from 'utils/helpers';
import getType from 'utils/helpers/getType';

import { SensorBattery, SensorStatus, SensorTrends } from 'entities/common';

type TabItem = {
  title: React.ReactNode;
  subtitle: string;
  value: string;
  Icon?: React.ReactNode;
};

// TODO reponsive styles
const SensorDetails: FC = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const {
    SensorDetails: { fullChartData, metrics, isLoading, isMetricsLoading, isChartLoading },
    // auth
  } = useSelector((store: IRootStore) => ({
    SensorDetails: store.sensorDetail,
    // auth: store.auth,
  }));
  const [activeDot, setActiveDot] = useState<number | null>(null);
  const [date, setDate] = useState<Date | [Date, Date]>(new Date());
  const [selectedType, setSelectedType] = useState(DateTypes.DAY);
  const [selectedTab, setSelectedTab] = useState<string>(SensorTabs.ALL);
  const [tabChanged, setTabChanged] = useState(false);
  const [nextUpdate, setNextUpdate] = useState(addMinutes(new Date(), 10));

  const statistics = useMemo(
    () => [
      {
        label: 'Plant type',
        value:
          PLANT_TYPES?.find((item) => item.value === metrics?.sensor?.plant_type)?.label ||
          metrics?.sensor?.plant_type ||
          '-',
      },
      {
        label: 'Plant name',
        value:
          PLANT_NAMES?.find((item) => item.value === metrics?.sensor?.plant_name)?.label ||
          metrics?.sensor?.plant_name ||
          '-',
      },
      {
        label: 'Type',
        value:
          SENSOR_TYPES?.find((item) => item.value === metrics?.sensor?.sensor_type)?.label || '-',
      },
      {
        label: 'Status',
        value:
          metrics?.sensor?.is_deleted || metrics?.sensor?.active === false
            ? StringHelper.capitalizeFirstLetter(SensorStatus.OFFLINE)
            : !metrics.items?.length
              ? '-'
              : StringHelper.capitalizeFirstLetter(
                (metrics.items[0]?.online_status ? SensorStatus.ONLINE : SensorStatus.OFFLINE) ||
                '',
              ),
        className:
          metrics?.sensor?.is_deleted || metrics?.sensor?.active === false
            ? 'sensor-color__red'
            : !metrics.items?.length
              ? ''
              : `sensor-color__${metrics.items[0]?.online_status ? 'blue' : 'red'}`,
      },
      {
        label: 'Battery charge',
        value: metrics?.sensor?.is_deleted
          ? '-'
          : !metrics.items?.length
            ? '-'
            : StringHelper.capitalizeFirstLetter(
              (metrics.items[0]?.battery_status ? SensorBattery.HIGH : SensorBattery.LOW) || '',
            ),
      },
      {
        label: 'Coordinates',
        value: !metrics.items?.length
          ? '-'
          : `${Number(metrics.items[0]?.latitude).toFixed(2) || 0}°, ${Number(metrics.items[0]?.longitude).toFixed(2) || 0}°`,
      },
      {
        label: 'Last update',
        value: metrics?.sensor?.is_deleted
          ? 'Deleted'
          : metrics.items?.[0]?.time
            ? formatLocalDateTime(metrics.items?.[0]?.time)
            : '-',
      },
    ],
    [metrics],
  );

  const tabs = useMemo((): TabItem[] => {
    const prevPoints = metrics?.items?.[1]?.points || [];
    const prevValueById: Record<string, number> = {};

    for (const point of prevPoints) {
      prevValueById[String(point.measurement_id)] = point.measurement_value;
    }

    const metricsTabs: TabItem[] =
      metrics?.items?.[0]?.points?.map((item) => {
        const prevValue = prevValueById[String(item.measurement_id)];
        let trend: SensorTrends | null = null;

        if (typeof prevValue === 'number') {
          if (item.measurement_value > prevValue) {
            trend = SensorTrends.UP;
          } else if (item.measurement_value < prevValue) {
            trend = SensorTrends.DOWN;
          }
        }

        return {
          title: (
            <>
              {item.measurement_value}
              <span className="sensor-details__info-size">{item.measurement_unit}</span>
            </>
          ),
          subtitle: item.measurement_name,
          value: item.measurement_id,
          Icon: trend ? trend === SensorTrends.UP ? <CourseUpIcon /> : <CourseDownIcon /> : null,
        };
      }) || [];

    if (metricsTabs.length > 1) {
      metricsTabs?.push({
        title: 'Full graph',
        subtitle: 'Graph',
        value: SensorTabs.ALL,
      });
    }

    return metricsTabs;
  }, [metrics]);

  const { subtitle, title } = useMemo(() => {
    let title = '';
    let subtitle = '';

    if (selectedTab === SensorTabs.ALL) {
      title = 'Full graph';
      subtitle = '';
    } else {
      const currentTab =
        SensorMeasurement?.[selectedTab as unknown as keyof typeof SensorMeasurement];

      title = currentTab?.label || '';
      subtitle = currentTab?.unit || '';
    }

    return { title, subtitle };
  }, [selectedTab]);

  const latestMetricTime = useMemo(
    () => (metrics?.items?.[0]?.time ? parseServerDate(metrics.items[0].time) : null),
    [metrics],
  );

  const points = useMemo(() => {
    const isDay = selectedType === DateTypes.DAY;

    if (!fullChartData?.series || !fullChartData.series.length) {
      return [];
    }

    const now = new Date();
    const cutoff = latestMetricTime && latestMetricTime < now ? latestMetricTime : now;

    let isCurrentPeriodSelected = false;

    if (selectedType === DateTypes.DAY && getType(date) === 'date') {
      isCurrentPeriodSelected =
        format(date as Date, 'dd.MM.yyyy') === format(now, 'dd.MM.yyyy');
    } else if (selectedType === DateTypes.WEEK && getType(date) === 'array') {
      const [start, end] = date as [Date, Date];
      isCurrentPeriodSelected = start <= now && now <= end;
    } else if (selectedType === DateTypes.MONTH && getType(date) === 'date') {
      isCurrentPeriodSelected =
        format(date as Date, 'MM.yyyy') === format(now, 'MM.yyyy');
    }

    const data = fullChartData.series.flatMap((item) =>
      item.points.map((element) => {
        const bucketStart = parseServerDate(element.bucket_start);

        const basePoint: { [x: string]: string | number | null; time: string } = {
          time: isDay
            ? formatLocalClock(element.bucket_start)
            : formatLocalDayMonth(element.bucket_start),
        };

        if (!isCurrentPeriodSelected || bucketStart <= cutoff) {
          basePoint[item.measurement_id] = element.value;
        }

        return basePoint;
      }),
    );

    const mergedData = mergeByTime(data);

    if (isDay) {
      const baseDayPoints: any[] = [];
      for (let i = 0; i < 24; i++) {
        const d = new Date(2000, 0, 1, i, 0);
        const timeStr = format(d, 'HH:mm');
        const match = mergedData.find((m) => m.time === timeStr);
        baseDayPoints.push(match || { time: timeStr });
      }
      return baseDayPoints;
    }

    return mergedData;
  }, [fullChartData?.series, selectedType, date, latestMetricTime]);

  const renderTab = useMemo(() => {
    if (selectedTab === SensorTabs.ALL) {
      return tabs?.filter((item) => item.value !== SensorTabs.ALL);
    }

    return tabs?.filter((item) => item.value === selectedTab);
  }, [selectedTab, tabs]);

  const handleSelectTab = (tab: string) => () => {
    setSelectedTab(tab);
  };

  const handleSelectType = (type: string) => {
    setSelectedType(type as DateTypes);

    if (type === DateTypes.DAY || type === DateTypes.MONTH) {
      setDate(new Date());
    } else {
      setDate([startOfISOWeek(new Date()), endOfISOWeek(new Date())]);
    }
  };

  const handleChangeDate = (value: Date | [Date, Date]) => {
    setDate(value);
  };

  useEffect(() => {
    dispatch(resetSensorDetails());

    return () => {
      dispatch(resetSensorDetails());
    };
  }, [dispatch]);

  // get last metric
  useEffect(() => {
    if (id) {
      dispatch(
        getSensorMetricsRequest({
          sensor_id: Number(id),
          query: QueryHelper.getQueryString({
            [QUERY_PARAMS.LIMIT]: 2,
          }),
        }),
      );
    }
  }, [dispatch, id]);

  // get full (full graph tab) chart data
  useEffect(() => {
    if (!id) {
      return;
    }

    const baseDate =
      getType(date) === 'date'
        ? (date as Date)
        : (date as [Date, Date])[0];

    dispatch(
      getSensorDetailFullChartRequest({
        sensor_id: Number(id),
        query: QueryHelper.getQueryString({
          [QUERY_PARAMS.MODE]: selectedType,
          [QUERY_PARAMS.TARGET_DATE]: new Date(
            format(new Date(baseDate), 'MM/dd/yyyy 12:00:00'),
          ).toISOString(),
          [QUERY_PARAMS.TZ]: getBrowserTimeZone(),
        }),
      }),
    );
  }, [dispatch, selectedType, date, id]);

  // update every 10 minutes
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (nextUpdate.valueOf() > new Date().valueOf()) {
      timer = setTimeout(() => {
        dispatch(
          getSensorMetricsRequest({
            sensor_id: Number(id),
            query: QueryHelper.getQueryString({
              [QUERY_PARAMS.LIMIT]: 2,
            }),
          }),
        );

        dispatch(
          getSensorDetailFullChartRequest({
            sensor_id: Number(id),
            query: QueryHelper.getQueryString({
              [QUERY_PARAMS.MODE]: selectedType,
              [QUERY_PARAMS.TARGET_DATE]: new Date(
                getType(date) === 'date'
                  ? format(new Date(date as Date), 'MM/dd/yyyy 12:00:00')
                  : format(new Date((date as [Date, Date])[0]), 'MM/dd/yyyy 12:00:00'),
              ).toISOString(),
              [QUERY_PARAMS.TZ]: getBrowserTimeZone(),
            }),
          }),
        );

        setNextUpdate(addMinutes(new Date(), 10));
      }, nextUpdate.valueOf() - new Date().valueOf());
    } else {
      setNextUpdate(addMinutes(new Date(), 10));
    }

    return () => {
      clearTimeout(timer);
    };
  }, [dispatch, nextUpdate, selectedType, date, id]);

  // set active first tab when render
  useEffect(() => {
    if (!tabChanged && tabs?.length) {
      setSelectedTab(tabs?.[0]?.value);
      setTabChanged(true);
    }
  }, [tabs, tabChanged]);

  // Стабилизируем функцию content для Tooltip
  const tooltipContent = useCallback(
    (props: any) => <CustomTooltip setActiveDot={setActiveDot} {...props} />,
    [setActiveDot],
  );

  return (
    <div className="sensor-details">
      <Loader isGlobal isLoading={isLoading || isChartLoading || isMetricsLoading} />

      <StatisticsSummary items={statistics} className="sensor-details__statistic" />

      <div
        className="sensor-details__info"
        style={{
          gridTemplateColumns:
            tabs?.length >= 6 ? `repeat(${6}, 1fr)` : `repeat(${tabs?.length}, 1fr)`,
        }}
      >
        {tabs?.map((item, index) => (
          <InfoCard
            key={index}
            {...item}
            active={selectedTab === item?.value}
            onClick={handleSelectTab(item?.value)}
          />
        ))}
      </div>

      <Card className="sensor-details__card">
        <div className="sensor-details__card-block">
          <div className="sensor-details__card-headline">
            <h2>{title}</h2>

            <span>{subtitle}</span>
          </div>

          <div className="sensor-details__card-actions">
            <ButtonGroup
              className="sensor-details__card-group"
              active={selectedType}
              buttons={[
                { text: 'Day', id: DateTypes.DAY },
                { text: 'Week', id: DateTypes.WEEK },
                { text: 'Month', id: DateTypes.MONTH },
              ]}
              onClick={handleSelectType}
            />

            <DateChanger date={date} onChange={handleChangeDate} type={selectedType} />
          </div>

          <div className="sensor-details__card-chart">
            {!isChartLoading && renderTab.length > 0 && (
              <ResponsiveContainer
                key={`${id}-${selectedTab}-${selectedType}`}
                width="100%"
                height="100%"
              >
                <AreaChart
                  data={points}
                  onMouseLeave={() => setActiveDot(null)}
                >
                  <XAxis dataKey="time" axisLine={false} tickLine={false} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    dataKey={selectedTab === SensorTabs.ALL ? undefined : selectedTab}
                  />

                  <Tooltip content={tooltipContent} />

                  {renderTab.map((item) => {
                    const measureKey = selectedTab === SensorTabs.ALL ? item.value : selectedTab;
                    const color = SensorMeasurement?.[measureKey]?.color || '#00C897';

                    return (
                      <Fragment key={item.value}>
                        <defs>
                          <linearGradient id={item.value} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" stopColor={color} stopOpacity={1} />
                            <stop offset="1" stopColor={color} stopOpacity={0} />
                          </linearGradient>
                        </defs>

                        <Area
                          type="linear"
                          dataKey={item.value}
                          stroke={color}
                          fill={`url(#${item.value})`}
                          fillOpacity={0.2}
                        />

                        {!!activeDot && selectedTab !== SensorTabs.ALL && (
                          <ReferenceLine y={activeDot} stroke={color} />
                        )}
                      </Fragment>
                    );
                  })}

                  <Brush
                    dataKey="time"
                    height={16}
                    stroke={SensorMeasurement?.[selectedTab === SensorTabs.ALL ? renderTab[0]?.value : selectedTab]?.color || '#00C897'}
                    travellerWidth={16}
                    fill="rgba(0, 0, 0, 0.05)"
                    style={{ userSelect: 'none' }}
                    className="sensor-details__card-chart__brush"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

function mergeByTime(
  data: {
    [x: string]: string | number | null;
    time: string;
  }[],
): Array<Record<string, any>> {
  const map = new Map<string, Record<string, any>>();

  for (const item of data) {
    const time = item.time;
    if (!map.has(time)) {
      map.set(time, { time });
    }

    Object.assign(map?.get(time) || {}, item);
  }

  return Array.from(map.values());
}

export default SensorDetails;
